import { renderHeaderAndText, renderExternal } from '@/tools/rendering';
import { setUrlToDataSource } from '@/tools/setUrlToDataSource';
import { mutationTypes, actionTypes, articleTypes, exhibitionTypes } from '@/types';
import logger from '@/tools/logger';

/**
 * This Action loads content via async ajax call or renders content depending on `exhibition.type`
 * and `article.type`
 */

export default async function loadContent(
	{ state, getters, commit, dispatch },
	{ articleID, exhibitionID, tourID }
) {
	// const articleID = state.currentArticleID;
	const language = getters.currentLanguage;
	const exhibition = state.dataSourceCFG.exhibitionMap[exhibitionID];
	const article = exhibition?.articleMap[articleID];

	if (!state.dataSourceURL) {
		logger.error('actionTypes.loadContent: DataSource was not loaded yet.');
		return;
	}

	if (!article) return;
	// Handling of all root pages like exhibitions, tours, all articles
	if (exhibition.type === exhibitionTypes.root) {
		if (articleID === 'exhibition-list') {
			dispatch(actionTypes.searchShowExhibitions, { options: { closeButton: false } });
			return;
		}
		// show all Tours of datasource
		if (articleID === 'tour-list') {
			dispatch(actionTypes.searchShowTours, { options: { closeButton: false } });
			return;
		}
		if (articleID === 'article-list') {
			dispatch(actionTypes.searchShowAllArticles, { options: { closeButton: false, sortBy: 'title' } });
			return;
		}
	}
	// Show all tours filtered by exhibition
	if (articleID === 'tour-list') {
		dispatch(actionTypes.searchShowTours, {
			options: { exhibitionID: state.currentExhibitionID, closeButton: false },
		});
		return;
	}
	if (article.type === articleTypes.tourList) {
		dispatch(actionTypes.searchShowTour, {
			exhibitionID: state.currentExhibitionID,
			tourID,
			options: { closeButton: false, sortBy: 'name' },
		});
		return;
	}
	if (article.type === articleTypes.articleList) {
		// Open search with depending on exhibition setting right focus to list all articles
		if (exhibition.type === exhibitionTypes.glossary) {
			dispatch(actionTypes.searchShowGlossary, { options: { closeButton: false } });
		} else if (exhibition.type === exhibitionTypes.calendar) {
			dispatch(actionTypes.searchShowCalendar, { options: { closeButton: false } });
		} else {
			dispatch(actionTypes.searchShowArticles, {
				exhibitionID: state.currentExhibitionID,
				options: { closeButton: false, sortBy: 'title' },
			});
		}
		return;
	}
	if (article.type === articleTypes.ar) {
		return '<div class="arDummyContent" style="background-color:#000000; width: 100%; height:100vh"></div>';
	}
	if (article.type === articleTypes.rawContent) {
		// Handle articles like glossaryTerm and calendarEntry.
		let languageContent = article.languageContent.filter(
			({ languageISO }) => languageISO === state.persistent.language
		);
		if (!languageContent) {
			languageContent = article.languageContent.filter(
				({ languageISO }) => languageISO === state.defaultLanguage
			);
		}
		if (!languageContent) languageContent = article.languageContent[0];

		const isGlossary = exhibition.type === exhibitionTypes.glossary;

		const content = renderHeaderAndText({
			imageSize: state.imageSize,
			hasHeader: !isGlossary,
			mediaObjectCover: article.mediaFileID
				? {
						id: article.mediaFileID,
						sourceUrl: setUrlToDataSource(`../media-${article.mediaFileID}`, state.dataSourceUrl),
				  }
				: undefined,
			pageTheme: article.pageTheme,
			content: isGlossary ? languageContent[0].content : languageContent.content,
			title: languageContent.title,
			description: languageContent.description,
			date: article.date,
			back: isGlossary
				? {
						command: 'command://setExhibitionLanding',
						label: getters.currentLanguage === 'de' ? 'Zurück' : 'Back',
				  }
				: null,
		});
		commit(mutationTypes.setContentForArticle, {
			id: articleID,
			content,
			language,
		});
		return content;
	}
	if (article.type === articleTypes.unityScene || article.type === articleTypes.custom) {
		const { id, version } = article;
		commit(mutationTypes.setContentForArticle, {
			id,
			content: renderExternal({
				url: setUrlToDataSource(`../article-${id}-${version}/customcontent/index.html`, state.dataSourceURL),
			}),
			language,
		});
		window.open(
			setUrlToDataSource(`../article-${id}-${version}/customcontent/index.html`, state.dataSourceURL),
			'_self'
		);
		return;
	}
	if (article.type === articleTypes.external) {
		const { id, url } = article;
		const content = renderExternal({
			url,
		});
		commit(mutationTypes.setContentForArticle, {
			id,
			content,
			language,
		});
		return content;
	}
	if (article.type === articleTypes.media) {
		if (state.phidget.mediaObjId) {
			dispatch(actionTypes.videoLoadAndPlay, {
				mediaID: state.phidget.mediaObjId,
			});
		}
	}

	await dispatch(actionTypes.fetchArticleContent, { articleID, exhibitionID });
}
